import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeSearch } from 'shared/actions/search';
import { getDealType } from 'shared/selectors/getDealType';
import { selectDate } from 'shared/selectors/results/selectDate';
import { selectSource } from 'shared/selectors/selectSource';
import { EDealType } from 'shared/types/dealType';
import { EMPTY_STATE_TELEMETRY } from 'shared/constants/emptyStateTelemetry';
import { trackPendingEmptyStateTags } from 'shared/tracking/emptyState';
import { IFiltersUpdatedEventDetail } from 'shared/types/microfrontends';
import { TThunkDispatch } from 'shared/types/redux';
import { useApplicationContext } from 'shared/utils/applicationContext';

const EVENT_NAME = 'filters:updated';

/**
 * Подписывается на событие из микрофронта на применение фильтров
 */
export const useFiltersUpdatedEvent = () => {
  const { microfrontendsAPI, microfrontendsRegistry, telemetry } = useApplicationContext();

  const dispatch = useDispatch<TThunkDispatch>();
  const dates = useSelector(selectDate);
  const dealType = useSelector(getDealType);
  const source = useSelector(selectSource);

  const isDailyRentWithoutDates = source === 'search_string' && dealType === EDealType.RentDaily && !dates;

  useEffect(() => {
    const onFiltersUpdated = ({ detail }: CustomEvent<IFiltersUpdatedEventDetail>) => {
      // Строго до makeSearch: он может увести на другой субдомен, и событие не успеет уйти
      const pendingTracking = trackPendingEmptyStateTags(detail.searchMeta.fullUrlWithoutSeo);

      /**
       * Сходимость обещанного и фактического: на теге было написано, сколько объявлений
       * найдётся после снятия, а мета уже принесла настоящее количество.
       */
      if (pendingTracking?.action === 'clearTag') {
        telemetry.increment(
          pendingTracking.count === detail.searchMeta.count
            ? EMPTY_STATE_TELEMETRY.countMatched
            : EMPTY_STATE_TELEMETRY.countMismatched,
        );
      }

      dispatch(makeSearch({ ...detail, withSource: isDailyRentWithoutDates }));

      if (microfrontendsRegistry.microfrontend('search-string-micro-frontend') && microfrontendsAPI) {
        microfrontendsAPI.getSearchStringMicrofrontendAPI().setFilterJsonQuery(detail.jsonQuery);
      }
    };

    addEventListener(EVENT_NAME, onFiltersUpdated);

    return () => {
      removeEventListener(EVENT_NAME, onFiltersUpdated);
    };
  }, [dispatch, isDailyRentWithoutDates, microfrontendsAPI, microfrontendsRegistry, telemetry]);
};
